import { Link } from "react-router-dom";
import { t, tf } from "@/lib/i18n";
import { Phone, Mail, MapPin, Instagram, Linkedin, Facebook, Youtube, Twitter, MessageCircle } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { NAV } from "@/components/Header";

const SOCIALS = [
  { key: "instagram", icon: Instagram }, { key: "linkedin", icon: Linkedin },
  { key: "facebook", icon: Facebook }, { key: "x", icon: Twitter }, { key: "youtube", icon: Youtube },
];

export default function Footer() {
  const { content: c } = useSite();
  return (
    <footer className="bg-[var(--ink)] text-[#E9E4D6] mt-24 grain" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid gap-12 md:grid-cols-12">
        <div className="md:col-span-4">
          <div className="flex items-center gap-4">
            <img src="/icon-192.png" alt={t("Kindsaver")} className="w-16 h-16 rounded-full" />
            <div>
              <div className="font-display text-2xl font-bold text-white">{t("kindsaver")}</div>
              <div className="text-sm text-[#BFD8B8]">{t("Kind. Clean. Bold.")}</div>
            </div>
          </div>
          <p className="mt-6 text-sm leading-relaxed text-[#C9C3B3] max-w-sm">
            {tf("Connecting citizens with local kabadiwalas. Starting in {city}, growing one neighbourhood at a time.", { city: c.launch_city })}
          </p>
          <div className="flex gap-2 mt-6">
            {SOCIALS.filter((s) => c[s.key]).map(({ key, icon: Icon }) => (
              <a key={key} href={c[key]} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/10 transition-colors" data-testid={`footer-social-${key}`} aria-label={key}>
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>

        <div className="md:col-span-2">
          <h4 className="text-white font-semibold mb-4">{t("Explore")}</h4>
          <ul className="space-y-2.5 text-sm">
            {NAV.slice(1).map((n) => (
              <li key={n.to}><Link to={n.to} className="link-u text-[#C9C3B3] hover:text-white" data-testid={`footer-link-${n.to.slice(1)}`}>{t(n.label)}</Link></li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-white font-semibold mb-4">{t("Get started")}</h4>
          <ul className="space-y-2.5 text-sm">
            <li><a href={c.consumer_app_url} className="link-u text-[#C9C3B3] hover:text-white" data-testid="footer-consumer-app">{t("Book a pickup · Sign up")}</a></li>
            <li><a href={c.partner_app_url} className="link-u text-[#C9C3B3] hover:text-white" data-testid="footer-partner-app">{t("Join as a kabadiwala partner")}</a></li>
            <li><Link to="/contact" className="link-u text-[#C9C3B3] hover:text-white" data-testid="footer-city-request">{t("Request Kindsaver in your city")}</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-white font-semibold mb-4">{t("Reach us")}</h4>
          <ul className="space-y-3 text-sm text-[#C9C3B3]">
            <li><a href={`tel:${c.phone.replace(/\s/g, "")}`} className="flex gap-2.5 hover:text-white" data-testid="footer-phone"><Phone size={16} className="mt-0.5 shrink-0" />{c.phone}</a></li>
            <li><a href={`https://wa.me/${c.whatsapp}`} target="_blank" rel="noreferrer" className="flex gap-2.5 hover:text-white" data-testid="footer-whatsapp"><MessageCircle size={16} className="mt-0.5 shrink-0" />{t("WhatsApp us")}</a></li>
            <li><a href={`mailto:${c.email}`} className="flex gap-2.5 hover:text-white" data-testid="footer-email"><Mail size={16} className="mt-0.5 shrink-0" />{c.email}</a></li>
            <li className="flex gap-2.5"><MapPin size={16} className="mt-0.5 shrink-0" />{c.address}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row justify-between gap-2 text-xs text-[#9C9787]">
          <span>© {new Date().getFullYear()} Kindsaver Ventures. {t("All rights reserved.")}</span>
          <span>{t("Made with care in Jaipur.")}</span>
        </div>
      </div>
    </footer>
  );
}
